import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { GuardiansExpandableSearch } from "./GuardiansExpandableSearch";
import { GuardiansFiltersButton } from "./GuardiansFiltersButton";
import { GuardiansToolbarAnimatedSlot } from "./GuardiansToolbarAnimatedSlot";
import { guardiansToolbarIconButtonClass } from "./guardiansToolbarButton";
import type { GuardiansFilterValues } from "./GuardiansFilterBar";
import type { GuardiansFilterOptions } from "./useGuardiansFilterOptions";
import type { ReactNode } from "react";
import type { AuditOriginFilter, GuardiansView } from "../guardiansParams";
import type { AuditBucket } from "../audits/useAudits";
import type { ScreeningBucket } from "../screenings/useScreenings";
import {
  guardiansFiltersToolbarVisible,
  type GuardiansTabFilterState,
  type TabFilterUrlPatch,
} from "./guardiansViewFilters";

export interface GuardiansToolbarSlotConfig {
  visible: boolean;
  content: ReactNode;
}

export interface GuardiansToolbarActionsProps {
  view: GuardiansView;
  filters: GuardiansFilterValues;
  tabFilters: GuardiansTabFilterState;
  filterOptions: GuardiansFilterOptions;
  onFiltersChange: (next: GuardiansFilterValues) => void;
  onAuditBucketChange: (bucket: AuditBucket) => void;
  onAuditOriginChange: (origin: AuditOriginFilter) => void;
  onScreeningBucketChange: (bucket: ScreeningBucket) => void;
  onTabFilterPatch: (patch: TabFilterUrlPatch) => void;
  onOpenSettings: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
  slot?: GuardiansToolbarSlotConfig | null;
}

export function GuardiansToolbarActions({
  view,
  filters,
  tabFilters,
  filterOptions,
  onFiltersChange,
  onAuditBucketChange,
  onAuditOriginChange,
  onScreeningBucketChange,
  onTabFilterPatch,
  onOpenSettings,
  search,
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  slot,
}: GuardiansToolbarActionsProps) {
  const filtersVisible = guardiansFiltersToolbarVisible(view);

  return (
    <div className="flex min-w-0 shrink items-center gap-2">
      {showSearch ? (
        <GuardiansExpandableSearch
          value={search}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
      ) : null}
      <GuardiansFiltersButton
        visible={filtersVisible}
        view={view}
        values={filters}
        tabFilters={tabFilters}
        options={filterOptions}
        onChange={onFiltersChange}
        onAuditBucketChange={onAuditBucketChange}
        onAuditOriginChange={onAuditOriginChange}
        onScreeningBucketChange={onScreeningBucketChange}
        onTabFilterPatch={onTabFilterPatch}
      />
      {slot ? (
        <GuardiansToolbarAnimatedSlot visible={slot.visible}>
          {slot.content}
        </GuardiansToolbarAnimatedSlot>
      ) : null}
      <button
        type="button"
        onClick={onOpenSettings}
        className={guardiansToolbarIconButtonClass}
        aria-label="Settings"
        data-testid="guardians-settings"
        data-toolbar-fixed
      >
        <FontAwesomeIcon icon={faGear} />
      </button>
    </div>
  );
}
